import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allIntegrationsKey, ss } from "consts";
import { IntegrationModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type IntegrationContextType = {
  loading: boolean;
  integrations?: IntegrationModel[];
  getIntegrations: () => Promise<void>;
};

const IntegrationContext = createContext({} as IntegrationContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<IntegrationModel[]>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setIntegrations(undefined);
    }
  }, [pIdOmni]);

  const getIntegrations = useCallback(async () => {
    let current: IntegrationModel[] = all[pIdOmni];

    if (!current)
      await AttendanceService.getIntegrations({ pIdOmni })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0) {
            current = data.omniIntegrationDataList;
            all[pIdOmni] = data.omniIntegrationDataList;
          } else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);

    setIntegrations(current);
    ss.setItem(allIntegrationsKey, JSON.stringify(all));

    setLoading(false);
  }, [all, pIdOmni, errorsResolver, warning, t]);

  return (
    <IntegrationContext.Provider
      value={{ loading, integrations, getIntegrations }}
    >
      {children}
    </IntegrationContext.Provider>
  );
};

export const IntegrationProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useIntegration = () => useContext(IntegrationContext);
